import * as Sentry from '@sentry/react';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
    ANALYSIS_CHALLENGES_REQUESTED,
    ANALYSIS_RESEARCHERS_REQUESTED,
    ANALYSIS_SEGMENTS_REQUESTED,
    TOP_OBJECTS_REQUESTED,
    TOP_MATERIALS_REQUESTED,
    TOP_BRANDS_REQUESTED,
    SEGMENTS_REPORT_REQUESTED,
    OBJECTS_TAGS_REQUESTED,
    BRANDS_TAGS_REQUESTED,
    MATERIALS_TAGS_REQUESTED,
    SEGMENTS_INDEX_REQUESTED,
    HEATMAP_LITTERS_REQUESTED,
} from './constants';
import {
    AnalysisChallengesFailed,
    AnalysisChallengesSucceeded,
    AnalysisResearchersFailed,
    AnalysisResearchersSucceeded,
    AnalysisSegmentsFailed,
    AnalysisSegmentsSucceeded,
    topBrandsSucceeded,
    topBrandsFailed,
    topMaterialsSucceeded,
    topMaterialsFailed,
    topObjectsSucceeded,
    topObjectsFailed,
    segmentReportSucceeded,
    segmentReportFailed,

    // report generation related actions
    topBrandsRequested,
    topMaterialsRequested,
    topObjectsRequested,
    objectsTagsFailed,
    objectsTagsSucceeded,
    brandsTagsFailed,
    brandsTagsSucceeded,
    brandsTagsRequested,
    materialsTagsRequested,
    objectsTagsRequested,
    materialsTagsSucceeded,
    materialsTagsFailed,
    segmentsIndexRequested,
    segmentsIndexSucceeded,
    segmentsIndexFailed,
    heatmapLittersRequested,
    heatmapLittersSucceeded,
    heatmapLittersFailed,
} from './actions';

import {
    getAnalysisSelectedChallenges,
    getAnalysisSelectedResearchers,
    getAnalysisSelectedSegments,
    getHeatmapStartDate,
    isComparisonActive,
    getAnalysisSelectedSegmentsCompare,
} from './selectors';
import {
    getAnalysisChalenges,
    getAnalysisResearchers,
    getAnalysisSegments,
    getAnalysTopTags,
    getAnalysSegmentTags,
    getAnalysSegmentIndex,
    getHeatmapLitters,
} from '../../api/';
import { parseClientError } from '../../api/utils';
import { getUserData } from '../auth/selectors';
import { logException } from '../../logger';

function* fetchChallenges() {
    try {
        const challenges = yield call(getAnalysisChalenges);

        if (challenges) {
            yield put(AnalysisChallengesSucceeded(challenges));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(AnalysisChallengesFailed(message));
    }
}

function* fetchResearchers() {
    const selectedChallenges = yield select(getAnalysisSelectedChallenges);

    try {
        const researchers = yield call(getAnalysisResearchers, {
            challengeIds:
                selectedChallenges.map((challenge) => challenge.id) || [],
        });

        if (researchers) {
            yield put(AnalysisResearchersSucceeded(researchers));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, {
            ...userData,
            challengeIds:
                selectedChallenges.map((challenge) => challenge.id) || [],
        });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(AnalysisResearchersFailed(message));
    }
}

function* fetchSegments() {
    let selectedChallenges = [];
    let selectedResearchers = [];
    try {
        selectedChallenges = yield select(getAnalysisSelectedChallenges);
        selectedResearchers = yield select(getAnalysisSelectedResearchers);

        const segments = yield call(getAnalysisSegments, {
            challengeIds:
                selectedChallenges.map((challenge) => challenge.id) || [],
            researcherIds:
                selectedResearchers.map((researcher) => researcher.id) || [],
        });

        if (segments) {
            yield put(AnalysisSegmentsSucceeded(segments));
        }

        return segments;
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, {
            ...userData,
            challengeIds:
                selectedChallenges.map((challenge) => challenge.id) || [],
            researcherIds:
                selectedResearchers.map((researcher) => researcher.id) || [],
        });
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(AnalysisSegmentsFailed(message));
    }
}

function* fetchTopObjects() {
    try {
        const selectedSegments = yield select(getAnalysisSelectedSegments);
        const comparisonActive = yield select(isComparisonActive);

        const selectedChallenges = yield select(getAnalysisSelectedChallenges);
        const selectedResearchers = yield select(
            getAnalysisSelectedResearchers,
        );
        const challengeIds =
            selectedChallenges.map((challenge) => challenge.id) || [];
        const researcherIds =
            selectedResearchers.map((researcher) => researcher.id) || [];

        let tagsCompare = [];
        if (comparisonActive) {
            const selectedSegmentsCompare = yield select(
                getAnalysisSelectedSegmentsCompare,
            );

            tagsCompare = yield call(getAnalysTopTags, {
                challengeIds,
                researcherIds,
                activityIds: selectedSegmentsCompare,
                taxonomy: 'OBJECT',
            });
        }

        const tags = yield call(getAnalysTopTags, {
            challengeIds,
            researcherIds,
            activityIds: selectedSegments,
            taxonomy: 'OBJECT',
        });

        if (tags) {
            yield put(topObjectsSucceeded(tags, tagsCompare));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(topObjectsFailed(message));
    }
}

function* fetchTopMaterials() {
    try {
        const selectedSegments = yield select(getAnalysisSelectedSegments);
        const selectedChallenges = yield select(getAnalysisSelectedChallenges);
        const selectedResearchers = yield select(
            getAnalysisSelectedResearchers,
        );
        const challengeIds =
            selectedChallenges.map((challenge) => challenge.id) || [];
        const researcherIds =
            selectedResearchers.map((researcher) => researcher.id) || [];
        const comparisonActive = yield select(isComparisonActive);

        let tagsCompare = [];
        if (comparisonActive) {
            const selectedSegmentsCompare = yield select(
                getAnalysisSelectedSegmentsCompare,
            );

            tagsCompare = yield call(getAnalysTopTags, {
                challengeIds,
                researcherIds,
                activityIds: selectedSegmentsCompare,
                taxonomy: 'MATERIAL',
            });
        }

        const tags = yield call(getAnalysTopTags, {
            challengeIds,
            researcherIds,
            activityIds: selectedSegments,
            taxonomy: 'MATERIAL',
        });

        if (tags) {
            yield put(topMaterialsSucceeded(tags, tagsCompare));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(topMaterialsFailed(message));
    }
}

function* fetchTopBrands() {
    try {
        const selectedSegments = yield select(getAnalysisSelectedSegments);
        const comparisonActive = yield select(isComparisonActive);

        const selectedChallenges = yield select(getAnalysisSelectedChallenges);
        const selectedResearchers = yield select(
            getAnalysisSelectedResearchers,
        );
        const challengeIds =
            selectedChallenges.map((challenge) => challenge.id) || [];
        const researcherIds =
            selectedResearchers.map((researcher) => researcher.id) || [];

        let tagsCompare = [];
        if (comparisonActive) {
            const selectedSegmentsCompare = yield select(
                getAnalysisSelectedSegmentsCompare,
            );

            tagsCompare = yield call(getAnalysTopTags, {
                challengeIds,
                researcherIds,
                activityIds: selectedSegmentsCompare,
                taxonomy: 'BRAND',
            });
        }

        const tags = yield call(getAnalysTopTags, {
            challengeIds,
            researcherIds,
            activityIds: selectedSegments,
            taxonomy: 'BRAND',
        });

        if (tags) {
            yield put(topBrandsSucceeded(tags, tagsCompare));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(topBrandsFailed(message));
    }
}

function* prepareSegmentForReport() {
    try {
        let segments = yield select(getAnalysisSelectedSegments);

        if (!segments.length) {
            const selectedChallenges = yield select(
                getAnalysisSelectedChallenges,
            );
            const selectedResearchers = yield select(
                getAnalysisSelectedResearchers,
            );

            segments = yield call(getAnalysisSegments, {
                challengeIds:
                    selectedChallenges.map((challenge) => challenge.id) || [],
                researcherIds:
                    selectedResearchers.map((researcher) => researcher.id) ||
                    [],
            });

            segments = segments.map((segment) => segment.id);
        }

        if (segments) {
            yield put(segmentReportSucceeded(segments));

            if (segments.length) {
                // report actions
                yield Promise.all([
                    yield put(topBrandsRequested()),
                    yield put(topMaterialsRequested()),
                    yield put(topObjectsRequested()),
                    yield put(brandsTagsRequested()),
                    yield put(materialsTagsRequested()),
                    yield put(objectsTagsRequested()),
                    yield put(segmentsIndexRequested()),
                    yield put(
                        heatmapLittersRequested(
                            new Date(
                                `1, 1, ${new Date().getFullYear()}`,
                            ).getTime(),
                        ),
                    ),
                ]);
            } else {
                yield Promise.all([
                    yield put(topBrandsRequested()),
                    yield put(topMaterialsRequested()),
                    yield put(topObjectsRequested()),
                    /** if no segments call success with empty data */
                    yield put(brandsTagsSucceeded([], [])),
                    yield put(materialsTagsSucceeded([], [])),
                    yield put(objectsTagsSucceeded([], [])),
                    yield put(segmentsIndexSucceeded({ group1: [] })),
                    yield put(heatmapLittersSucceeded([])),
                ]);
            }
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(segmentReportFailed(message));
    }
}

function* fetchBrandTags() {
    try {
        const selectedSegments = yield select(getAnalysisSelectedSegments);
        const comparisonActive = yield select(isComparisonActive);

        let tagsCompare = [];
        if (comparisonActive) {
            const selectedSegmentsCompare = yield select(
                getAnalysisSelectedSegmentsCompare,
            );

            tagsCompare = yield call(getAnalysSegmentTags, {
                activityIds: selectedSegmentsCompare,
                taxonomy: 'BRAND',
            });
        }

        const tags = yield call(getAnalysSegmentTags, {
            activityIds: selectedSegments,
            taxonomy: 'BRAND',
        });

        if (tags) {
            yield put(brandsTagsSucceeded(tags, tagsCompare));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(brandsTagsFailed(message));
    }
}

function* fetchMaterialTags() {
    try {
        const selectedSegments = yield select(getAnalysisSelectedSegments);
        const comparisonActive = yield select(isComparisonActive);

        let tagsCompare = [];
        if (comparisonActive) {
            const selectedSegmentsCompare = yield select(
                getAnalysisSelectedSegmentsCompare,
            );

            tagsCompare = yield call(getAnalysSegmentTags, {
                activityIds: selectedSegmentsCompare,
                taxonomy: 'MATERIAL',
            });
        }

        const tags = yield call(getAnalysSegmentTags, {
            activityIds: selectedSegments,
            taxonomy: 'MATERIAL',
        });

        if (tags) {
            yield put(materialsTagsSucceeded(tags, tagsCompare));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(materialsTagsFailed(message));
    }
}

function* fetchObjectTags() {
    try {
        const selectedSegments = yield select(getAnalysisSelectedSegments);
        const comparisonActive = yield select(isComparisonActive);

        let tagsCompare = [];
        if (comparisonActive) {
            const selectedSegmentsCompare = yield select(
                getAnalysisSelectedSegmentsCompare,
            );

            tagsCompare = yield call(getAnalysSegmentTags, {
                activityIds: selectedSegmentsCompare,
                taxonomy: 'OBJECT',
            });
        }

        const tags = yield call(getAnalysSegmentTags, {
            activityIds: selectedSegments,
            taxonomy: 'OBJECT',
        });

        if (tags) {
            yield put(objectsTagsSucceeded(tags, tagsCompare));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(objectsTagsFailed(message));
    }
}

function* fetchSegmentIndex() {
    try {
        const selectedSegments = yield select(getAnalysisSelectedSegments);
        const comparisonActive = yield select(isComparisonActive);

        let params = {
            group1: selectedSegments.toString(),
        };

        if (comparisonActive) {
            const selectedSegmentsCompare = yield select(
                getAnalysisSelectedSegmentsCompare,
            );

            params = {
                group1: selectedSegments.toString(),
                group2: selectedSegmentsCompare.toString(),
            };
        }

        const index = yield call(getAnalysSegmentIndex, params);

        if (index) {
            let segmentIndex = {
                group1: [],
            };

            if (comparisonActive) {
                const groupOne = {
                    index: index.group1.reduce((acc, cur) => {
                        if (parseFloat(cur.index))
                            return acc + parseFloat(cur.index);

                        return acc;
                    }, 0),
                    id: 'grp-1',
                    name: 'GROUP 1',
                };

                const groupTwo = {
                    index: index.group2.reduce((acc, cur) => {
                        if (parseFloat(cur.index))
                            return acc + parseFloat(cur.index);

                        return acc;
                    }, 0),
                    id: 'grp-2',
                    name: 'GROUP 2',
                };

                segmentIndex.group1.push(groupOne);
                segmentIndex.group1.push(groupTwo);
            } else {
                segmentIndex = index;
            }

            yield put(segmentsIndexSucceeded(segmentIndex));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(segmentsIndexFailed(message));
    }
}

function* fetchHeatmapLitters() {
    try {
        let selectedSegments = yield select(getAnalysisSelectedSegments);
        const startDate = yield select(getHeatmapStartDate);

        const comparisonActive = yield select(isComparisonActive);

        if (comparisonActive) {
            const selectedSegmentsCompare = yield select(
                getAnalysisSelectedSegmentsCompare,
            );
            selectedSegments = selectedSegments.concat(selectedSegmentsCompare);
        }

        const heatmapLitters = yield call(getHeatmapLitters, {
            activityIds: selectedSegments,
            startDate,
        });

        if (heatmapLitters) {
            yield put(heatmapLittersSucceeded(heatmapLitters));
        }
    } catch (e) {
        Sentry.captureException(e);
        const userData = yield select(getUserData);
        logException(e, userData);
        const error = parseClientError(e);
        const message = error && error.message ? error.message : error;
        yield put(heatmapLittersFailed(message));
    }
}

export default function*() {
    yield all([
        takeLatest(ANALYSIS_CHALLENGES_REQUESTED, fetchChallenges),
        takeLatest(ANALYSIS_RESEARCHERS_REQUESTED, fetchResearchers),
        takeLatest(ANALYSIS_SEGMENTS_REQUESTED, fetchSegments),
        takeLatest(TOP_OBJECTS_REQUESTED, fetchTopObjects),
        takeLatest(TOP_MATERIALS_REQUESTED, fetchTopMaterials),
        takeLatest(TOP_BRANDS_REQUESTED, fetchTopBrands),
        takeLatest(SEGMENTS_REPORT_REQUESTED, prepareSegmentForReport),
        takeLatest(OBJECTS_TAGS_REQUESTED, fetchObjectTags),
        takeLatest(BRANDS_TAGS_REQUESTED, fetchBrandTags),
        takeLatest(MATERIALS_TAGS_REQUESTED, fetchMaterialTags),
        takeLatest(SEGMENTS_INDEX_REQUESTED, fetchSegmentIndex),
        takeLatest(HEATMAP_LITTERS_REQUESTED, fetchHeatmapLitters),
    ]);
}
